import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, Paper, Grid, Button, CircularProgress, Alert, TextField, MenuItem, Stack, FormControl, InputLabel, Select } from '@mui/material';
import InvoiceTable from '../../../components/Tables/InvoiceTable';
import { getCustomer, getInvoices, getShops, getApiError } from '../../../utils/api';

const CustomerPage = () => {
  const { customerNumber } = useParams();
  const [customer, setCustomer] = useState(null);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoreShops, setLoadingMoreShops] = useState(false);
  const [error, setError] = useState('');
  const [shopPage, setShopPage] = useState(1);
  const [hasMoreShops, setHasMoreShops] = useState(true);
  const [filterInvoice, setFilterInvoice] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const loadShops = async () => {
    try {
      setLoadingMoreShops(true);
      const res = await getShops(shopPage, 20);
      const newShops = res.data?.results || [];
      setShops(prev => shopPage === 1 ? newShops : [...prev, ...newShops]);
      setHasMoreShops(newShops.length === 20);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMoreShops(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    const fetchData = async () => {
      try {
        const [customerRes, invoicesRes] = await Promise.all([
          getCustomer(customerNumber),
          getInvoices()
        ]);
        
        if (!mounted) return;
        
        setCustomer(customerRes.data);
        const invoices = ((invoicesRes.data && invoicesRes.data.results) || [])
          .filter(inv => inv.customerNumber === customerNumber);
        setCustomerInvoices(invoices);
        
        await loadShops(); // Initial shop load
      } catch (err) {
        if (mounted) setError(getApiError(err));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [customerNumber]);

  const handleLoadMoreShops = () => {
    setShopPage(prev => prev + 1);
    loadShops();
  };

  if (loading) {
    return <Box p={3} textAlign="center"><CircularProgress /></Box>;
  }
  if (error) {
    return <Box p={3}><Alert severity="error">{error}</Alert></Box>;
  }
  if (!customer) {
    return <Box p={3}><Typography variant="h5">Customer not found.</Typography></Box>;
  }

  const totalSpent = customerInvoices.reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);
  const categories = Array.from(new Set(customerInvoices.map(inv => inv.category)));
  const locations = Array.from(new Set((customer.locationsVisited || [])));

  // Filtered invoices
  const filteredInvoices = customerInvoices.filter(inv => {
    if (filterInvoice && inv.invoiceId && !inv.invoiceId.toString().includes(filterInvoice)) return false;
    if (filterCategory && filterCategory !== 'all' && inv.category !== filterCategory) return false;
    return true;
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'top', justifyContent: 'center', py: { xs: 2, md: 6 }, px: { xs: 1, md: 0 } }}>
      <Button component={Link} to="/analytics" variant="outlined" sx={{ mb: 2 }}>
        &larr; Back to Analytics
      </Button>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, width: '100%', borderRadius: 4, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mx: 'auto', mb: 3 }}>
        <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom>
          Customer: {customerNumber}
        </Typography>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6} md={3}><Typography><b>Total Spent:</b> ₹{totalSpent.toLocaleString()}</Typography></Grid>
          <Grid item xs={12} sm={6} md={3}><Typography><b>Invoices:</b> {customerInvoices.length}</Typography></Grid>
          <Grid item xs={12} sm={6} md={3}><Typography><b>Categories:</b> {categories.join(', ')}</Typography></Grid>
          <Grid item xs={12} sm={6} md={3}><Typography><b>Locations:</b> {locations.join(', ')}</Typography></Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
        <Typography variant="h6" mb={2}>All Invoices for {customerNumber}</Typography>
        {/* Filters */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          <TextField
            label="Search Invoice #"
            size="small"
            value={filterInvoice}
            onChange={e => setFilterInvoice(e.target.value)}
            sx={{ minWidth: 160 }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '0.875rem' }}>Category</InputLabel>
            <Select 
              value={filterCategory} 
              label="Category" 
              onChange={e => setFilterCategory(e.target.value)}
              sx={{
                '& .MuiSelect-select': {
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  fontSize: '0.875rem'
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0'
                  },
                  '&:hover fieldset': {
                    borderColor: '#bdbdbd'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main'
                  }
                },
                '& .MuiInputLabel-root': {
                  '&.Mui-focused': {
                    color: 'primary.main'
                  },
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                    backgroundColor: 'white',
                    padding: '0 4px'
                  }
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    '& .MuiMenuItem-root': {
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      fontSize: '0.875rem'
                    }
                  }
                }
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {categories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
        <InvoiceTable rows={filteredInvoices} shops={shops} />
        {hasMoreShops && !loadingMoreShops && (
          <Box textAlign="center" mt={2}>
            <Button variant="outlined" onClick={handleLoadMoreShops} color="primary">
              Load More Shops
            </Button>
          </Box>
        )}
        {loadingMoreShops && (
          <Box textAlign="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CustomerPage; 
